import React, { useState } from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import { fmtMoney, fmtMonthLabel } from '../helpers';
import type { CashFlowPoint } from '../types';
import CashFlowTooltip from './CashFlowTooltip';

type Props = {
  points: ReadonlyArray<CashFlowPoint>;
  threshold?: number;
  startingBalance?: number;
};

type BalancePoint = {
  monthIso: string;
  balance: number;
  moneyIn: number;
  moneyOut: number;
  projected: boolean;
};

function toBalanceSeries(points: ReadonlyArray<CashFlowPoint>, start: number): BalancePoint[] {
  let running = start;
  return points.map((p) => {
    running += (p.moneyIn - p.moneyOut);
    return {
      monthIso: p.monthIso,
      balance: running,
      moneyIn: p.moneyIn,
      moneyOut: p.moneyOut,
      projected: p.projected,
    };
  });
}

export default function CashFlowArea({ points, threshold = 0, startingBalance = 0 }: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const series = toBalanceSeries(points, startingBalance);

  if (series.length === 0) {
    return (
      <div style={{ height: 150, display: 'grid', placeItems: 'center', color: C.sub, fontSize: 13, fontStyle: 'italic', fontFamily: F.family }}>
        No cash flow data yet.
      </div>
    );
  }

  const w = 400;
  const h = 150;
  const padBottom = 10;
  const yMax = Math.max(...series.map((p) => p.balance), threshold, 1);
  const yMin = Math.min(...series.map((p) => p.balance), 0);
  const yRange = Math.max(yMax - yMin, 1);
  const xStep = w / Math.max(series.length - 1, 1);

  const toX = (i: number) => i * xStep;
  const toY = (v: number) => (h - padBottom) - ((v - yMin) / yRange) * (h - padBottom - 10);
  const thresholdY = toY(threshold);

  const firstProjIdx = series.findIndex((p) => p.projected);
  const actuals = firstProjIdx === -1 ? series : series.slice(0, firstProjIdx + 1);
  const projs = firstProjIdx === -1 ? [] : series.slice(firstProjIdx);
  const actualOffset = 0;
  const projOffset = firstProjIdx === -1 ? 0 : firstProjIdx;

  const buildLine = (pts: BalancePoint[], offset: number) =>
    pts.map((p, i) => (i === 0 ? 'M' : 'L') + ' ' + toX(offset + i).toFixed(1) + ' ' + toY(p.balance).toFixed(1)).join(' ');

  const buildFill = (pts: BalancePoint[], offset: number) => {
    if (pts.length === 0) return '';
    const line = buildLine(pts, offset);
    const lastX = toX(offset + pts.length - 1).toFixed(1);
    const firstX = toX(offset).toFixed(1);
    return line + ' L ' + lastX + ' ' + h + ' L ' + firstX + ' ' + h + ' Z';
  };

  return (
    <div style={{ fontFamily: F.family }}>
      <div style={{ position: 'relative', height: h, margin: '10px 0 2px' }}>
        <svg viewBox={'0 0 ' + w + ' ' + h} preserveAspectRatio="none" width="100%" height="100%">
          {/* Threshold horizontal line */}
          <line x1="0" y1={thresholdY} x2={w} y2={thresholdY} stroke={C.faint} strokeWidth="1" strokeDasharray="3 3" />

          <defs>
            <linearGradient id="cfAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#15A08C" />
              <stop offset="100%" stopColor="#15A08C" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Projected area (dashed line + light fill) */}
          {projs.length > 0 && (
            <>
              <path d={buildFill(projs, projOffset)} fill="url(#cfAreaGrad)" opacity="0.18" />
              <path d={buildLine(projs, projOffset)} fill="none" stroke={C.teal} strokeWidth="2.2" strokeDasharray="5 4" strokeLinecap="round" />
            </>
          )}

          {/* Actual area */}
          <path d={buildFill(actuals, actualOffset)} fill="url(#cfAreaGrad)" opacity="0.4" />
          <path d={buildLine(actuals, actualOffset)} fill="none" stroke={C.teal} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        {/* Hover zones per month */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
          {series.map((p, i) => (
            <div
              key={p.monthIso}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{ flex: 1, position: 'relative', cursor: 'pointer' }}
            >
              {hoverIdx === i && (
                <>
                  <span style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: C.ink, opacity: 0.25 }} />
                  <CashFlowTooltip
                    title={fmtMoney(p.balance)}
                    subtitle={'Cash balance for ' + fmtMonthLabel(p.monthIso, true) + (p.projected ? ' (projected)' : ' (actual)')}
                    rows={[
                      { color: C.teal, label: 'Money in',  amount: fmtMoney(p.moneyIn) },
                      { color: C.red,  label: 'Money out', amount: fmtMoney(p.moneyOut) },
                      { color: C.faint,label: 'Threshold', amount: fmtMoney(threshold) },
                    ]}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.ink, fontWeight: 600, marginTop: 4 }}>
        {series.filter((_, i) => i % Math.ceil(series.length / 6) === 0).map((p) => (
          <span key={p.monthIso}>{fmtMonthLabel(p.monthIso, true)}</span>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: C.ink, marginTop: 12, fontWeight: 500 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.teal }} /> Cash balance
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.teal, opacity: 0.35 }} /> Projected balance
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.faint }} /> Threshold
        </span>
      </div>
    </div>
  );
}
